import React, { useRef, useState } from 'react';
import { Navigation, Autoplay, EffectFade } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';

const Slider = () => {
    const { t } = useTranslation();

    const slides = [
        {
            type: 'image',
            title: [t("home.slider.slide1.title1"), t("home.slider.slide1.title2")],
            text: [t("home.slider.slide1.text1"), t("home.slider.slide1.text2"), t("home.slider.slide1.text3")],
            src: '/images/slider/0.png'
        },
        {
            type: 'video',
            title: [""],
            text: [""],
            src: '/images/slider/banner_video3_1.mp4'
        },
        {
            type: 'image',
            title: [""],
            text: [""],
            src: '/images/slider/2.jpg'
        },
        {
            type: 'video',
            title: [""],
            text: [""],
            src: '/images/slider/banner_video2_1a.mp4'
        },
        {
            type: 'image',
            title: [""],
            text: [""],
            src: '/images/slider/1.jpg'
        },
    ];

    const swiperRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const videoRefs = useRef([]);

    React.useEffect(() => {
        const swiper = swiperRef.current;
        if (!swiper) return;
        if (slides[activeIndex].type === 'video') {
            swiper.autoplay.stop();
            const videoEl = videoRefs.current[activeIndex];
            if (videoEl) {
                videoEl.currentTime = 0;
                videoEl.play();
            }
        } else {
            swiper.autoplay.start();
            videoRefs.current.forEach((ref, idx) => {
                if (ref && idx !== activeIndex) {
                    ref.pause && ref.pause();
                }
            });
        }
    }, [activeIndex]);

    return (
        <motion.div className='h-[101vh] grid grid-cols-10 relative'>
            <div className='col-start-1 col-span-10'>
                <Swiper
                    modules={[Navigation, Autoplay, EffectFade]}
                    onSwiper={(swiper) => (swiperRef.current = swiper)}
                    onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                    navigation={{
                        nextEl: '.right-hook',
                        prevEl: '.left-hook',
                    }}
                    autoplay={{
                        delay: 3000,
                        disableOnInteraction: false,
                    }}
                    effect="fade"
                    fadeEffect={{ crossFade: true }}
                    className='h-full w-full'
                >
                    {slides.map((slide, index) => (
                        <SwiperSlide key={index} className='!flex h-full relative'>
                            <motion.div
                                key={index}
                                className='grid grid-cols-10 w-full h-full  relative items-center z-30'
                            >
                                <div className='col-start-2 col-span-9 h-96 w-full flex flex-col text-[#0A0101] justify-center '>
                                    <motion.h2
                                        initial={{ opacity: 0, y: -50 }}
                                        animate={{
                                            opacity: activeIndex === index ? 1 : 0,
                                            y: activeIndex === index ? 0 : -50,
                                        }}
                                        transition={{ duration: 1, ease: 'easeInOut' }}
                                        className=' text-3xl md:text-56 '>
                                        <span>{slide.title[0]}</span>
                                        <br />
                                        <span className='font-thin'>{slide.title[1]}</span>
                                    </motion.h2>
                                    <motion.p
                                        initial={{ opacity: 0, y: -50 }}
                                        animate={{
                                            opacity: activeIndex === index ? 1 : 0,
                                            y: activeIndex === index ? 0 : -50,
                                        }}
                                        transition={{ duration: 1, delay: 0.5, ease: 'easeInOut' }}
                                        className='mt-5 font-light text-20'>
                                        {slide.text[0]}
                                        <br />
                                        {slide.text[1]}
                                        <br />
                                        {slide.text[2]}
                                    </motion.p>
                                </div>
                            </motion.div>
                            <div className='w-full h-full absolute object-cover'>
                                {slide.type === 'image' ? (
                                    <motion.img
                                        initial={{ opacity: 0, x: 50 }}
                                        animate={{
                                            opacity: activeIndex === index ? 1 : 0,
                                            x: activeIndex === index ? 0 : 50,
                                        }}
                                        transition={{ duration: 1, delay: 1, ease: 'easeInOut' }}
                                        src={slide.src}
                                        className='object-cover h-full w-full' />
                                ) : (
                                    slide.src ? (
                                        <motion.video
                                            ref={el => videoRefs.current[index] = el}
                                            initial={{ opacity: 0, x: 50 }}
                                            animate={{
                                                opacity: activeIndex === index ? 1 : 0,
                                                x: activeIndex === index ? 0 : 50,
                                            }}
                                            transition={{ duration: 1, delay: 1, ease: 'easeInOut' }}
                                            className='h-full w-full object-cover'
                                            muted
                                            onEnded={() => {
                                                swiperRef.current?.slideNext();
                                                swiperRef.current?.autoplay.start();
                                            }}
                                        >
                                            <source src={slide.src} type="video/mp4" />
                                            {t("home.slider.videoNotSupported")}
                                        </motion.video>
                                    ) : null
                                )}
                            </div>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            <div className='absolute bottom-16 right-[20%] flex space-x-14 z-50'>
                <div onClick={() => swiperRef.current?.slidePrev()} className="cursor-pointer">
                    <img src='/images/icons/left-hook.svg' className='w-4' />
                </div>
                <div onClick={() => swiperRef.current?.slideNext()} className="cursor-pointer">
                    <img src='/images/icons/right-hook.svg' className='w-4' />
                </div>
            </div>

            <div className='absolute bottom-9 left-[10%] flex space-x-2 z-50'>
                {slides.map((_, index) => (
                    <div
                        key={index}
                        className={`h-3 w-3 rounded-full border-customGray4 border ${index === activeIndex ? 'bg-customGray4' : ''
                            }`}
                    ></div>
                ))}
            </div>
        </motion.div>
    );
};

export default Slider;
